import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { projectsApi, tasksApi, calendarApi } from "@/lib/api";
import ProjectHealth from "@/components/ProjectHealth";
import { AlertCircle, ChevronRight } from "lucide-react";

/**
 * Member landing surface — replaces the executive HQ grid for non-admins.
 * Uses the SAME scoped endpoints (/projects, /tasks, /calendar); the backend
 * already returns only what this user is allowed to see, so this view can
 * never leak unauthorized data.
 */
export default function MemberHome() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [p, t, ev] = await Promise.all([
        projectsApi.list(),
        tasksApi.list(),
        calendarApi.list({}),
      ]);
      setProjects(p.data || []);
      setTasks(t.data || []);
      setEvents(ev.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
  const doneStatuses = new Set(["Done", "Completed"]);
  const openTasks = tasks.filter(t => !doneStatuses.has(t.status));
  const overdue = openTasks.filter(t => t.due_date && t.due_date < today);
  const upcoming = openTasks.filter(t => t.due_date && t.due_date >= today && t.due_date <= in7);
  const upcomingEvents = (events || []).filter(e => {
    const d = (e.starts_at || "").slice(0, 10);
    return d && d >= today && d <= in7;
  });

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="member-home">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="text-eyebrow">Your workspace</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">My Projects</h1>
        </div>

        {loading && <div className="text-white/50">Loading…</div>}

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {projects.length === 0 && !loading && (
              <div className="text-sm text-white/50 border border-dashed border-white/10 rounded-xl p-8 text-center" data-testid="member-empty">
                You haven't been assigned to any projects yet.
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {projects.map(p => {
                const myTasks = tasks.filter(t => t.project_id === p.id);
                const nextTask = myTasks.filter(t => !doneStatuses.has(t.status)).sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))[0];
                return (
                  <Link key={p.id} to={`/entity/${p.entity_id}?tab=projects`} data-testid={`member-project-${p.id}`}
                        className="block p-5 rounded-xl border border-white/10 bg-[#151A23] hover:bg-white/[0.04] transition">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display font-semibold text-lg truncate">{p.name}</h3>
                        <div className="text-xs text-white/50 mt-0.5">{p.description || "\u00A0"}</div>
                      </div>
                      {p.health && <ProjectHealth health={p.health} compact testid={`member-health-${p.id}`}/>}
                    </div>
                    {(p.mission || p.goals) && (
                      <div className="mt-3 text-xs text-white/70 space-y-1">
                        {p.mission && <div><span className="text-white/40 uppercase text-[10px] tracking-wider">Mission:</span> {p.mission}</div>}
                        {p.goals && <div><span className="text-white/40 uppercase text-[10px] tracking-wider">Goals:</span> {p.goals}</div>}
                      </div>
                    )}
                    {nextTask && (
                      <div className="mt-3 pt-3 border-t border-white/10 text-xs">
                        <span className="text-white/40 uppercase text-[10px] tracking-wider">Next:</span> <span className="text-white/85">{nextTask.name}</span>
                        {nextTask.due_date && <span className="text-white/50"> · due {nextTask.due_date}</span>}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          <aside className="space-y-6" data-testid="member-right-now">
            <div className="p-4 rounded-xl border border-white/10 bg-[#151A23]">
              <div className="text-eyebrow mb-2">Right Now</div>
              {overdue.length === 0 && upcoming.length === 0 && upcomingEvents.length === 0 && (
                <div className="text-xs text-white/50">Nothing urgent on your plate.</div>
              )}
              {overdue.length > 0 && (
                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wider text-red-400 mb-1">Overdue</div>
                  {overdue.slice(0, 5).map(t => (
                    <div key={t.id} className="text-sm text-white/85 flex items-center gap-2" data-testid={`member-overdue-${t.id}`}>
                      <AlertCircle className="w-3 h-3 text-red-400"/> <span className="truncate">{t.name}</span>
                      <span className="text-xs text-white/40">{t.due_date}</span>
                    </div>
                  ))}
                </div>
              )}
              {upcoming.length > 0 && (
                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-1">This week</div>
                  {upcoming.slice(0, 5).map(t => (
                    <div key={t.id} className="text-sm text-white/85 flex items-center gap-2" data-testid={`member-upcoming-${t.id}`}>
                      <ChevronRight className="w-3 h-3 text-white/40"/> <span className="truncate">{t.name}</span>
                      <span className="text-xs text-white/40">{t.due_date}</span>
                    </div>
                  ))}
                </div>
              )}
              {upcomingEvents.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">Milestones</div>
                  {upcomingEvents.slice(0, 5).map(e => (
                    <div key={e.id} className="text-sm text-white/85 truncate" data-testid={`member-event-${e.id}`}>
                      {(e.starts_at || "").slice(0, 10)} · {e.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
